import { z } from 'zod';
import { AccountSparkBrand } from '../account';
import { PagedApiResponseBody, IPagingProperties } from '../http';

/**
 * Types for Spark Wizard Details Step Data endpoint
 * Fetches spark brands for the vendor account
 */

export const getDetailsStepDataPathParamsSchema = z.object({
  accountId: z.string().min(1),
});

export const getDetailsStepDataQueryParamsSchema = z.object({
  originatorAccountId: z.string().optional(),
});

export type GetDetailsStepDataPathParams = z.infer<typeof getDetailsStepDataPathParamsSchema>;
export type GetDetailsStepDataQueryParams = z.infer<typeof getDetailsStepDataQueryParamsSchema>;

export interface DetailsStepDataResponseBody {
  sparkBrands: AccountSparkBrand[];
}

/**
 * Types for Spark Wizard Products Step Data endpoint
 * This endpoint provides server-side filtered products for both retailer and vendor accounts
 */

export const getProductsStepDataPathParamsSchema = z.object({
  accountId: z.string().min(1), // This is the retailerAccountId
});

export const getProductsStepDataBodySchema = z.object({
  // Vendor account info (optional, present for vendor accounts)
  vendorAccountId: z.string().optional(),
  brandId: z.string().optional(),

  // Filters (all optional for initial load)
  primaryFilter: z.enum(['brands', 'categories']).optional(),
  brandIds: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  lastSoldAt: z.string().optional(), // '-60days' | '-90days' | '-120days' | 'allTime'
  anchorDate: z.string().optional(), // ISO date string to use as anchor for lastSoldAt lookback
  hideSampleProducts: z.boolean().optional(),
  productNameContains: z.array(z.string()).optional(),
  productNameDoesNotContain: z.array(z.string()).optional(),
  productNameFilters: z
    .array(
      z.object({
        filterType: z.enum(['CONTAINS', 'DOES_NOT_CONTAIN', 'STARTS_WITH', 'ENDS_WITH']),
        text: z.string(),
      }),
    )
    .optional(), // Advanced product name filters
  searchTerm: z.string().optional(), // Search across product name, brand, and category

  // Pagination
  offset: z.number().optional().default(0),
  limit: z.number().optional().default(1000),
});

export type GetProductsStepDataPathParams = z.infer<typeof getProductsStepDataPathParamsSchema>;
export type GetProductsStepDataBody = z.infer<typeof getProductsStepDataBodySchema>;

// Minimal product response
export interface SparkWizardProduct {
  _id: string;
  name: string;
  brandId: string;
  brandName: string;
  categoryId: string;
  categoryName: string;
  lastSoldAt?: string;
  price?: number;
  sample: boolean;
}

export interface ProductsStepDataResponseBody extends PagedApiResponseBody<SparkWizardProduct> {
  meta: IPagingProperties & {
    oldProductsCount: number;
    nonQualifyingProductsCount: number;
  };
}

/**
 * Types for Spark Wizard Filter Options endpoint
 * This endpoint provides brand and category filter options without loading full product data
 */

export const getFilterOptionsPathParamsSchema = z.object({
  accountId: z.string().min(1),
});

export const getFilterOptionsBodySchema = z.object({
  vendorAccountId: z.string().optional(),
  brandId: z.string().optional(),
  lastSoldAt: z.string().optional(),
  anchorDate: z.string().optional(), // ISO date string to use as anchor for lastSoldAt lookback
  hideSampleProducts: z.boolean().optional(),
});

export type GetFilterOptionsPathParams = z.infer<typeof getFilterOptionsPathParamsSchema>;
export type GetFilterOptionsBody = z.infer<typeof getFilterOptionsBodySchema>;

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterOptionsResponseBody {
  brands: FilterOption[];
  categories: FilterOption[];
}

/**
 * Types for Spark Wizard Participants Step Data endpoint
 * This endpoint fetches locations and participants data for the participants step
 */

export const getParticipantsStepDataPathParamsSchema = z.object({
  accountId: z.string().min(1),
});

export const getParticipantsStepDataQueryParamsSchema = z.object({
  groupId: z.string().optional(), // Retailer ID for vendor accounts
  sparkBrandId: z.string().optional(), // Filter by specific brand
});

export type GetParticipantsStepDataPathParams = z.infer<
  typeof getParticipantsStepDataPathParamsSchema
>;
export type GetParticipantsStepDataQueryParams = z.infer<
  typeof getParticipantsStepDataQueryParamsSchema
>;

export interface ParticipantsStepDataResponseBody {
  locations: any[]; // IPosLocation[]
  participants: any[]; // IAccountUser[]
  metadata: {
    totalLocationCount: number;
    totalParticipantCount: number;
    enrolledParticipantCount: number;
  };
}

/**
 * Types for Spark Wizard Old Products endpoint
 * This endpoint fetches products that match filter rules but haven't been sold recently
 */

export const getOldProductsPathParamsSchema = z.object({
  accountId: z.string().min(1), // This is the retailerAccountId
});

export const getOldProductsBodySchema = z.object({
  // Vendor account info (optional, present for vendor accounts)
  vendorAccountId: z.string().optional(),
  brandId: z.string().optional(),

  // Filters (all optional for initial load)
  brandIds: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  lastSoldAt: z.string().optional(), // '-60days' | '-90days' | '-120days' | 'allTime'
  anchorDate: z.string().optional(), // ISO date string to use as anchor for lastSoldAt lookback
  hideSampleProducts: z.boolean().optional(),
  productNameContains: z.array(z.string()).optional(),
  productNameDoesNotContain: z.array(z.string()).optional(),
  productNameFilters: z
    .array(
      z.object({
        filterType: z.enum(['CONTAINS', 'DOES_NOT_CONTAIN', 'STARTS_WITH', 'ENDS_WITH']),
        text: z.string(),
      }),
    )
    .optional(), // Advanced product name filters
  searchTerm: z.string().optional(), // Search across product name, brand, and category

  // Pagination
  offset: z.number().optional().default(0),
  limit: z.number().optional().default(1000),
});

export type GetOldProductsPathParams = z.infer<typeof getOldProductsPathParamsSchema>;
export type GetOldProductsBody = z.infer<typeof getOldProductsBodySchema>;

export interface OldProductsResponseBody extends PagedApiResponseBody<SparkWizardProduct> {}

/**
 * Types for Spark Wizard Non-Qualifying Products endpoint
 * This endpoint fetches products that don't match the current filter rules
 */

export const getNonQualifyingProductsPathParamsSchema = z.object({
  accountId: z.string().min(1), // This is the retailerAccountId
});

export const getNonQualifyingProductsBodySchema = z.object({
  // Vendor account info (optional, present for vendor accounts)
  vendorAccountId: z.string().optional(),
  brandId: z.string().optional(),

  // Filters (all optional for initial load)
  brandIds: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  lastSoldAt: z.string().optional(), // '-60days' | '-90days' | '-120days' | 'allTime'
  anchorDate: z.string().optional(), // ISO date string to use as anchor for lastSoldAt lookback
  hideSampleProducts: z.boolean().optional(),
  productNameContains: z.array(z.string()).optional(),
  productNameDoesNotContain: z.array(z.string()).optional(),
  searchTerm: z.string().optional(), // Search across product name, brand, and category

  // Pagination
  offset: z.number().optional().default(0),
  limit: z.number().optional().default(1000),
});

export type GetNonQualifyingProductsPathParams = z.infer<
  typeof getNonQualifyingProductsPathParamsSchema
>;
export type GetNonQualifyingProductsBody = z.infer<typeof getNonQualifyingProductsBodySchema>;

export interface NonQualifyingProductsResponseBody
  extends PagedApiResponseBody<SparkWizardProduct> {}

/**
 * Types for Spark Wizard Commission Step Products endpoint
 * This endpoint fetches filtered products for the commission step based on filters from the products step
 */

export const getCommissionProductsPathParamsSchema = z.object({
  accountId: z.string().min(1), // Retailer account ID
});

export const getCommissionProductsBodySchema = z.object({
  // Vendor account info (optional, for vendor accounts)
  vendorAccountId: z.string().optional(),
  brandId: z.string().optional(),

  // Product IDs from products step (selected products)
  posProductIds: z.array(z.string()).optional(),

  // Product IDs that already have commission values > 0
  // Used to sort products WITHOUT commissions first (so users see what needs attention)
  productsWithCommissions: z.array(z.string()).optional(),

  // Filters from products step (to re-apply filtering)
  productFilters: z
    .object({
      primaryFilter: z.enum(['brands', 'categories']).optional(),
      brandIds: z.array(z.string()).optional(),
      categoryIds: z.array(z.string()).optional(),
      lastSoldAt: z.string().optional(),
      productNameContains: z.array(z.string()).optional(),
      productNameDoesNotContain: z.array(z.string()).optional(),
    })
    .optional(),

  // Spark metadata
  startDate: z.string().optional(), // For lastSoldAt filtering

  // Server-side filtering (for commission step toolbar filters)
  searchTerm: z.string().optional(),

  // Pagination (for infinite scroll in variable commission mode)
  offset: z.number().optional().default(0),
  limit: z.number().optional().default(50),
});

export type GetCommissionProductsPathParams = z.infer<typeof getCommissionProductsPathParamsSchema>;
export type GetCommissionProductsBody = z.infer<typeof getCommissionProductsBodySchema>;

export interface CommissionProductsResponseBody extends PagedApiResponseBody<SparkWizardProduct> {
  // Extends PagedApiResponseBody to support pagination with data[] and meta
}

/**
 * Types for Bulk Participants Summary endpoint (Optimized for bulk sparks)
 * Returns lightweight retailer summaries with counts instead of full employee lists
 * This enables instant rendering of 150+ retailers without loading all employees upfront
 */

export const getBulkParticipantsSummaryPathParamsSchema = z.object({
  accountId: z.string().min(1), // Vendor account ID
});

export const getBulkParticipantsSummaryBodySchema = z.object({
  retailerIds: z.array(z.string()).min(1), // All retailer IDs in the selected market
  sparkBrandId: z.string().optional(), // Filter by specific spark brand
});

export type GetBulkParticipantsSummaryPathParams = z.infer<
  typeof getBulkParticipantsSummaryPathParamsSchema
>;
export type GetBulkParticipantsSummaryBody = z.infer<typeof getBulkParticipantsSummaryBodySchema>;

/** Location summary for bulk participant selection */
export interface BulkLocationSummary {
  id: string;
  name: string;
  displayName?: string;
  employeeCount: number;
}

/** Lightweight retailer summary for bulk participant selection */
export interface BulkRetailerSummary {
  id: string;
  name: string;
  locationCount: number;
  employeeCount: number;
  hasBrandLink: boolean;
  lastTransactionDate?: string;
  isActive: boolean; // Derived from hasBrandLink + lastTransactionDate (within 60 days)
  /** Locations for this retailer - used to build the tree without additional API calls */
  locations: BulkLocationSummary[];
}

export interface BulkParticipantsSummaryResponseBody {
  retailers: Record<string, BulkRetailerSummary>;
}

/**
 * Types for Retailer Employees endpoint (On-demand loading)
 * Fetches paginated employees for a single retailer when expanded in the UI
 * This supports lazy loading and virtualization for large employee lists
 */

export const getRetailerEmployeesPathParamsSchema = z.object({
  accountId: z.string().min(1), // Vendor account ID
});

export const getRetailerEmployeesQueryParamsSchema = z.object({
  groupId: z.string().min(1), // Retailer account ID
  sparkBrandId: z.string().optional(), // Filter by specific spark brand
  offset: z.coerce.number().optional().default(0),
  limit: z.coerce.number().optional().default(50),
  searchTerm: z.string().optional(), // Search employees by name
});

export type GetRetailerEmployeesPathParams = z.infer<typeof getRetailerEmployeesPathParamsSchema>;
export type GetRetailerEmployeesQueryParams = z.infer<typeof getRetailerEmployeesQueryParamsSchema>;

/** Minimal employee info for bulk participant selection */
export interface BulkEmployee {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  posEmployeeProfileIds: string[];
  locationIds: string[];
  isEnrolled: boolean;
  /** Last location where employee made a sale (for tooltip) */
  lastSoldLocationId?: string;
  /** Last transaction date (for tooltip) */
  lastTransactionDate?: string;
  /** Location IDs managed by this employee (for manager chip) */
  managedLocationIds?: string[];
}

/** Location info for bulk participant selection */
export interface BulkLocation {
  id: string;
  name: string;
  displayName?: string;
  groupId: string;
}

export interface RetailerEmployeesResponseBody {
  employees: BulkEmployee[];
  locations: BulkLocation[];
  meta: IPagingProperties;
}

/**
 * Types for Location Employees endpoint (On-demand loading)
 * Fetches employees for a single location when expanded in the UI
 * This is the most granular lazy loading - only load employees when location is clicked
 */

export const getLocationEmployeesPathParamsSchema = z.object({
  accountId: z.string().min(1), // Vendor account ID
});

export const getLocationEmployeesQueryParamsSchema = z.object({
  locationId: z.string().min(1), // Location ID
  retailerAccountId: z.string().min(1), // Retailer account ID (for validation)
  sparkBrandId: z.string().optional(), // Filter by specific spark brand
  offset: z.coerce.number().optional().default(0),
  limit: z.coerce.number().optional().default(100),
});

export type GetLocationEmployeesPathParams = z.infer<typeof getLocationEmployeesPathParamsSchema>;
export type GetLocationEmployeesQueryParams = z.infer<typeof getLocationEmployeesQueryParamsSchema>;

export type LocationEmployeesResponseBody = PagedApiResponseBody<BulkEmployee>;
